/** Helpers to share a link or a picture to WhatsApp and other apps. */

interface NativeShare {
  share(o: { title?: string; text?: string; url?: string; dialogTitle?: string }): Promise<unknown>;
}

interface NativeFs {
  writeFile(o: { path: string; data: string; directory: string }): Promise<{ uri: string }>;
}
type Cap = { Plugins?: { Share?: NativeShare & { share(o: { files?: string[] } & Record<string, unknown>): Promise<unknown> }; Filesystem?: NativeFs } };

function cap(): Cap | undefined {
  return (window as unknown as { Capacitor?: Cap }).Capacitor;
}

function nativePlugin(): NativeShare | null {
  return cap()?.Plugins?.Share ?? null;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.readAsDataURL(blob);
  });
}

/**
 * The picture is shared exactly as it is - the whole page width, nothing added at its sides
 * (earlier versions put empty space beside tall pictures; that made the page look narrow / cropped).
 */
export async function fitForChat(blob: Blob): Promise<Blob> {
  return blob;
}

/** In the Android app: write the picture to a temporary file and open the system share sheet with it. */
async function nativeShareFile(blob: Blob, title: string, text: string): Promise<boolean> {
  const c = cap()?.Plugins;
  if (!c?.Share || !c.Filesystem) return false;
  try {
    const data = await blobToBase64(blob);
    const f = await c.Filesystem.writeFile({ path: `clipping-${Date.now()}.jpg`, data, directory: 'CACHE' });
    await c.Share.share({ title, text, files: [f.uri], dialogTitle: 'Share or save clipping' });
    return true;
  } catch (e) {
    return (e as Error).name === 'AbortError' || /cancel/i.test(String((e as Error).message));
  }
}

export function canNativeShare(): boolean {
  return !!nativePlugin() || typeof (navigator as unknown as { share?: unknown }).share === 'function';
}

/** Opens the phone/computer share sheet for a link. Returns false when not available. */
export async function shareLink(title: string, text: string, url: string): Promise<boolean> {
  try {
    const p = nativePlugin();
    if (p) {
      await p.share({ title, text, url, dialogTitle: 'Share' });
      return true;
    }
    const n = navigator as unknown as { share?: (d: { title: string; text: string; url: string }) => Promise<void> };
    if (n.share) {
      await n.share({ title, text, url });
      return true;
    }
  } catch (e) {
    if ((e as Error).name === 'AbortError') return true; // user closed the sheet
  }
  return false;
}

/** Shares the picture itself where the browser allows it (most phones). */
export async function shareImageFile(original: Blob, title: string, text: string): Promise<boolean> {
  const blob = await fitForChat(original);
  if (await nativeShareFile(blob, title, text)) return true;
  try {
    const file = new File([blob], 'clipping.jpg', { type: blob.type || 'image/jpeg' });
    const n = navigator as unknown as {
      canShare?: (d: { files: File[] }) => boolean;
      share?: (d: { files: File[]; title: string; text: string }) => Promise<void>;
    };
    if (n.canShare && n.share && n.canShare({ files: [file] })) {
      await n.share({ files: [file], title, text });
      return true;
    }
  } catch (e) {
    if ((e as Error).name === 'AbortError') return true;
  }
  return false;
}

export interface Target {
  id: string;
  label: string;
  href: (text: string, url: string) => string;
}

export const TARGETS: Target[] = [
  { id: 'whatsapp', label: 'WhatsApp', href: (t, u) => `https://wa.me/?text=${encodeURIComponent(t ? t + '\n' + u : u)}` },
  { id: 'telegram', label: 'Telegram', href: (t, u) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}` },
  { id: 'facebook', label: 'Facebook', href: (_t, u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}` },
  { id: 'x', label: 'X', href: (t, u) => `https://twitter.com/intent/tweet?text=${encodeURIComponent(t)}&url=${encodeURIComponent(u)}` },
  { id: 'linkedin', label: 'LinkedIn', href: (_t, u) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}` },
  { id: 'email', label: 'Email', href: (t, u) => `mailto:?subject=${encodeURIComponent(t)}&body=${encodeURIComponent(t ? t + '\n\n' + u : u)}` },
  { id: 'reddit', label: 'Reddit', href: (t, u) => `https://www.reddit.com/submit?url=${encodeURIComponent(u)}&title=${encodeURIComponent(t || 'Newspaper')}` },
  { id: 'gmail', label: 'Gmail', href: (t, u) => `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(t)}&body=${encodeURIComponent(t ? t + '\n\n' + u : u)}` },
  { id: 'outlook', label: 'Outlook', href: (t, u) => `https://outlook.live.com/mail/0/deeplink/compose?subject=${encodeURIComponent(t)}&body=${encodeURIComponent(t ? t + '\n\n' + u : u)}` },
  { id: 'line', label: 'LINE', href: (_t, u) => `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(u)}` },
  { id: 'viber', label: 'Viber', href: (t, u) => `viber://forward?text=${encodeURIComponent(t ? t + ' ' + u : u)}` },
  { id: 'sms', label: 'SMS', href: (t, u) => `sms:?&body=${encodeURIComponent(t ? t + ' ' + u : u)}` },
];

export async function copyText(s: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(s);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = s;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

/**
 * Copies a web address so that it pastes as a real, clickable link (Word, Gmail, Docs, WhatsApp Web ...)
 * and still pastes as the plain address in editors that only take text.
 */
export async function copyLink(url: string): Promise<boolean> {
  const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const html = `<a href="${esc(url)}">${esc(url)}</a>`;
  try {
    const CI = (window as unknown as { ClipboardItem?: new (d: Record<string, Blob>) => unknown }).ClipboardItem;
    if (CI && navigator.clipboard && 'write' in navigator.clipboard) {
      await (navigator.clipboard as unknown as { write(i: unknown[]): Promise<void> }).write([
        new CI({ 'text/html': new Blob([html], { type: 'text/html' }), 'text/plain': new Blob([url], { type: 'text/plain' }) }),
      ]);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    // older browsers: select a real anchor on the page and copy that selection
    const box = document.createElement('div');
    box.contentEditable = 'true';
    box.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
    box.innerHTML = html;
    document.body.appendChild(box);
    const range = document.createRange();
    range.selectNodeContents(box);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    const ok = document.execCommand('copy');
    sel?.removeAllRanges();
    document.body.removeChild(box);
    if (ok) return true;
  } catch {
    /* fall through */
  }
  return copyText(url);
}

export function downloadBlob(blob: Blob, name: string): void {
  // Android WebView cannot download blobs - its share sheet offers "Save" / "Share" instead.
  if (cap()?.Plugins?.Filesystem && cap()?.Plugins?.Share) {
    void nativeShareFile(blob, 'Newspaper clipping', 'Newspaper clipping');
    return;
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

/** True on phones and tablets, where the system share sheet can carry the picture into WhatsApp etc. */
export function isMobileDevice(): boolean {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || !!cap()?.Plugins?.Share;
}

async function toPng(blob: Blob): Promise<Blob> {
  if (blob.type === 'image/png') return blob;
  const bmp = await createImageBitmap(blob);
  const c = document.createElement('canvas');
  c.width = bmp.width;
  c.height = bmp.height;
  const g = c.getContext('2d');
  if (!g) throw new Error('canvas');
  g.drawImage(bmp, 0, 0);
  return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('png'))), 'image/png'));
}

/** Puts the picture on the clipboard so it can be pasted (Ctrl+V) into WhatsApp Web, Telegram, mail ... */
export async function copyImage(blob: Blob): Promise<boolean> {
  try {
    const CI = (window as unknown as { ClipboardItem?: new (d: Record<string, Promise<Blob> | Blob>) => unknown }).ClipboardItem;
    const clip = navigator.clipboard as unknown as { write?: (items: unknown[]) => Promise<void> };
    if (!CI || !clip.write) return false;
    await clip.write([new CI({ 'image/png': fitForChat(blob).then(toPng) })]);
    return true;
  } catch {
    return false;
  }
}

export interface PictureTarget {
  id: string;
  label: string;
  open: string; // where to paste the picture on a computer
}

export const PICTURE_TARGETS: PictureTarget[] = [
  { id: 'whatsapp', label: 'WhatsApp', open: 'https://web.whatsapp.com/' },
  { id: 'telegram', label: 'Telegram', open: 'https://web.telegram.org/' },
  { id: 'facebook', label: 'Facebook', open: 'https://www.facebook.com/' },
  { id: 'x', label: 'X', open: 'https://x.com/compose/post' },
  { id: 'linkedin', label: 'LinkedIn', open: 'https://www.linkedin.com/feed/' },
  { id: 'instagram', label: 'Instagram', open: 'https://www.instagram.com/' },
  { id: 'reddit', label: 'Reddit', open: 'https://www.reddit.com/submit' },
  { id: 'gmail', label: 'Gmail', open: 'https://mail.google.com/mail/?view=cm&fs=1' },
  { id: 'outlook', label: 'Outlook', open: 'https://outlook.live.com/mail/0/deeplink/compose' },
  { id: 'email', label: 'Email', open: 'mailto:' },
  { id: 'sms', label: 'SMS', open: 'sms:' },
];

/** True when the address only works on this computer / network, so other people cannot open a link to it. */
export function isLocalAddress(url: string): boolean {
  try {
    const h = new URL(url).hostname;
    return h === 'localhost' || h.endsWith('.local') || /^127\./.test(h) || /^10\./.test(h) || /^192\.168\./.test(h) || /^172\.(1[6-9]|2\d|3[01])\./.test(h);
  } catch {
    return false;
  }
}

/** Where "send the picture" opens the app, with the link already typed in (the picture is pasted next to it). */
export function openWithLink(t: PictureTarget, url: string): string {
  const u = encodeURIComponent(url);
  if (t.id === 'whatsapp') return `https://wa.me/?text=${u}`;
  if (t.id === 'telegram') return `https://t.me/share/url?url=${u}`;
  if (t.id === 'email') return `mailto:?body=${u}`;
  if (t.id === 'facebook') return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
  if (t.id === 'x') return `https://twitter.com/intent/tweet?url=${u}`;
  if (t.id === 'linkedin') return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
  if (t.id === 'reddit') return `https://www.reddit.com/submit?url=${u}`;
  if (t.id === 'gmail') return `https://mail.google.com/mail/?view=cm&fs=1&body=${u}`;
  if (t.id === 'outlook') return `https://outlook.live.com/mail/0/deeplink/compose?body=${u}`;
  if (t.id === 'sms') return `sms:?&body=${u}`;
  return t.open;
}
